import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { documentFileExtension, imageFileExtension, maxFileUploadSize } from "../Api/FileConstant";
import { InputIcon } from "primereact/inputicon";
import { Toast } from "primereact/toast";
import DocumentViewModal from "./DocumentViewModal";
import { Ripple } from "primereact/ripple";

interface FileInputProps {
    id: string;
    label: string;
    labelHtmlFor: string;
    required?: boolean;
    value: string;
    onUpload?: (file: File) => void;
    onClear?: () => void;
    error?: string;
    hasMaxFileSize?: boolean;
    maxFileSize?: number;
    inputClassName?: string;
    containerClassName?: string;
    toast: React.RefObject<Toast>;
    canUploadImage?: boolean;
    onlyImageUpload?: boolean;
}

const FileInput = forwardRef<FileUpload, FileInputProps>(({
    id,
    label,
    labelHtmlFor,
    required,
    value,
    onUpload,
    onClear,
    error,
    toast,
    hasMaxFileSize = true,
    maxFileSize = maxFileUploadSize,
    inputClassName,
    containerClassName,
    canUploadImage,
    onlyImageUpload
}, ref) => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>('');
    const [uploadedFileExtension, setUploadedFileExtension] = useState<string>('');
    const fileUploadRef = useRef<FileUpload>(null);

    const [showDocumentViewModal, setShowDocumentViewModal] = useState<boolean>(false);
    const [showImageViewModal, setShowImageViewModal] = useState<boolean>(false);

    useImperativeHandle(ref, () => fileUploadRef.current as FileUpload);

    useEffect(() => {
        if (uploadedFile === null) {
            fileUploadRef.current?.clear();
        }
    }, [uploadedFile]);

    const handleViewDocument = (filePath: string) => {
        setShowDocumentViewModal(true);
    };

    const handleImageViewModal = (filePath: string) => {
        setShowImageViewModal(true);
    }

    const handleUpload = (e: FileUploadHandlerEvent) => {
        const file = e.files[0];
        if (file) {
            if (maxFileSize && file.size > maxFileSize) {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'File Upload error',
                        detail: `File size should not exceed ${maxFileSize / 1000000} MB`,
                        life: 3000
                    });
                }
                setUploadedFileName('');
                setUploadedFile(null);
                setUploadedFileExtension('');
                if (fileUploadRef.current) {
                    fileUploadRef.current.clear();
                }
                return;
            }
            setUploadedFileName(file.name);
            setUploadedFileExtension(file.type.split('/')[1]);
            setUploadedFile(file);
            if (onUpload) {
                onUpload(file);
            }
        }
    };

    const handleClear = () => {
        setUploadedFileName('');
        setUploadedFileExtension('');
        setUploadedFile(null);
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }

        if (onClear) {
            onClear();
        }
    };

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setShowImageViewModal(false);
            }
        };

        if (showImageViewModal) {
            window.addEventListener("keydown", handleEscape);
        }

        return () => {
            window.removeEventListener("keydown", handleEscape);
        };
    }, [showImageViewModal]);

    return (
        <React.Fragment>
            <div className={`page_form_group ${containerClassName}`}>
                <label htmlFor={labelHtmlFor} className={`custom_form_label ${required ? 'is_required' : ''}`}>{label}</label>

                <div className="file_upload_container">
                    <FileUpload
                        id={id}
                        ref={fileUploadRef}
                        mode="basic"
                        name="document"
                        customUpload
                        uploadHandler={handleUpload}
                        accept={onlyImageUpload ? imageFileExtension : (documentFileExtension + (canUploadImage ? (', ' + imageFileExtension) : ''))}
                        auto
                        disabled={uploadedFileName ? true : false}
                        chooseLabel={`${uploadedFileName ? 'Uploaded' : 'Browse'}`}
                        className={`doc_file_upload ${uploadedFileName ? 'uploaded' : ''} ${inputClassName}`}
                    />

                    {uploadedFile !== null ? (
                        <span className="upload_file_name uploaded"
                            onClick={() => {
                                if (uploadedFileExtension === 'pdf') {
                                    handleViewDocument(URL.createObjectURL(uploadedFile));
                                } else {
                                    handleImageViewModal(URL.createObjectURL(uploadedFile));
                                }
                            }}>
                            {uploadedFileName}
                        </span>
                    ) : (
                        <span className="upload_file_name">Select file</span>
                    )}
                    {uploadedFile &&
                        <InputIcon
                            className="bi bi-x clear_button icon_info"
                            onClick={handleClear}
                            title="Clear file"
                        ></InputIcon>
                    }
                </div>
                <span className="file_upload_info flex">
                    <i className="bi bi-info-circle"></i>
                    <span>
                        <span>
                            Acceptable file format : {onlyImageUpload ? imageFileExtension : (documentFileExtension + (canUploadImage ? (', ' + imageFileExtension) : ''))}
                        </span>
                        <br />
                        <span>
                            {hasMaxFileSize && (
                                <React.Fragment>
                                    {`${maxFileSize && 'Maximum file size is ' + maxFileSize / 1000000 + ' MB'}`}
                                </React.Fragment>
                            )}
                        </span>
                    </span>
                </span>

                {error && <small className="form_error_msg">{error}</small>}
            </div>

            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showDocumentViewModal}
                onClose={() => setShowDocumentViewModal(false)}
                fileUrl={uploadedFile ? URL.createObjectURL(uploadedFile) : ''}
            />

            {/* Image view modal */}
            <div className={`image_gallery_modal ${showImageViewModal ? 'show' : ''}`}
                onClick={() => setShowImageViewModal(false)}>
                <button
                    className="image_gallery_close_button p-ripple"
                    onClick={() => setShowImageViewModal(false)}
                >
                    <i className="bi bi-x-lg"></i>
                    <Ripple />
                </button>
                <div className="image_view_modal_container container">
                    <img
                        src={uploadedFile ? URL.createObjectURL(uploadedFile) : ''}
                        alt={uploadedFileName}
                        className={`image_view_modal_image ${showImageViewModal ? 'show' : ''}`}
                    />
                </div>
            </div>
        </React.Fragment>
    )
});

export default FileInput;